import {
  Box,
  Button,
  Card,
  CardContent,
  Menu,
  MenuItem,
  Skeleton,
  Stack,
  Typography,
} from '@mui/material'
import { useState } from 'react'
import { Divider, IconButton } from 'utoniq-ui'
import { Icons } from 'utoniq-ui'

import {
  ConsoleMembershipQuery,
  ConsoleProductDetailFragment,
  Maybe,
  MembershipSubscriptionProduct,
} from '@/clients/utoniq-core/schema'
import {
  ArtistHeaderItemsLeft,
  ArtistHeaderItemsRight,
  BottomSafeArea,
  Head,
} from '@/components/layouts'
import { ConsoleBaseLayout } from '@/components/layouts/ConsoleBaseLayout'
import { MembershipProductBox } from '@/components/organisms'
import { ScrollFetcher } from '@/components/organisms/functionals'
import { ArtistThemeProvider } from '@/components/styles'
import { User as AuthUser, useRouter, useTranslation } from '@/core'
import { Cell, CellFrame, Template } from '@/core/architecture'

export * from './fragments'

export type State = {
  artistId: string
  currentUser: Maybe<AuthUser>
  artist: ConsoleMembershipQuery['artist']
  totalFavoriteMembers: number
  products: ConsoleProductDetailFragment[]
  loading: boolean
  membership: Pick<MembershipSubscriptionProduct, 'productId'>[] | undefined
}
export type Action = {
  onChangeRequirePersonalInformation: () => Promise<void>
  onExportCsv: () => Promise<void>
}

export const ConsoleMembershipTemplate: Template<State, Action> = ({
  state,
  onChangeRequirePersonalInformation,
  onExportCsv,
}) => {
  const { t } = useTranslation('consoleMemberShip')
  const { routerNames } = useRouter()
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)

  const options = [
    {
      label: state.artist?.subscription?.requirePersonalInformation
        ? t('notCollectPersonalInfo')
        : t('collectPersonalInfo'),
      onClick() {
        onChangeRequirePersonalInformation()
        setAnchorEl(null)
      },
    },
    {
      label: t('exportCsv', 'CSVエクスポート'),
      disabled: !state.products?.length,
      onClick() {
        onExportCsv()
        setAnchorEl(null)
      },
    },
  ]

  return (
    <ArtistThemeProvider artistId={state.artistId}>
      <Head title={routerNames.ConsoleMembership} />
      <ConsoleBaseLayout
        header={{
          left: <ArtistHeaderItemsLeft />,
          right: <ArtistHeaderItemsRight artistId={state.artistId} />,
        }}
        customNavigation={
          <FixedBottomCell
            disabled={state.products?.length >= 3 || state.loading}
          />
        }
      >
        <Stack sx={{ pt: 2, px: 5, pb: 3 }}>
          <Stack
            flex={1}
            direction="row"
            spacing={2}
            justifyContent="space-between"
            alignItems="center"
            py={3}
          >
            <Typography variant="h3" width="100%">
              {t('title')}
            </Typography>
            <IconButton.More
              edge="end"
              onClick={event => setAnchorEl(event.currentTarget)}
            />
          </Stack>
          <Menu
            open={!!anchorEl}
            anchorEl={anchorEl}
            onClose={() => setAnchorEl(null)}
            transformOrigin={{ horizontal: 'right', vertical: 'top' }}
            anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
          >
            {options.map(({ label, ...option }) => (
              <MenuItem {...option} sx={{ width: 200 }} key={label}>
                {label}
              </MenuItem>
            ))}
          </Menu>
          <Divider sx={{ borderColor: 'grey.50', borderWidth: 4 }} />
          <MembershipIconsCell />
          <Divider sx={{ borderColor: 'grey.50', borderWidth: 4 }} />
          <Box sx={{ pb: 4 }}></Box>

          <MembershipNoteCell
            loading={state.loading && !state.artist}
            totalPlan={state.products?.length}
            requirePersonalInformation={
              !!state.artist?.subscription?.requirePersonalInformation
            }
            totalFavoriteMembers={state.totalFavoriteMembers}
            enableFavoriteMembers={
              state.artist?.settings?.features?.subscription
                ?.enableFavoriteMembers ?? false
            }
            enableDigitalMembershipCard={
              !!state.artist?.settings?.features?.subscription
                ?.enableDigitalMembershipCard
            }
          />
          <ScrollFetcher
            loading={state.loading}
            hasNextPage={false}
            componentProps={{ spacing: 6 }}
            onFetchMore={async () => {}}
            nodes={state.products?.map((product, index) => (
              <MembershipProductBox
                key={[product.productId, index].join(':')}
                product={product}
              />
            ))}
            loadingComponent={
              <Stack spacing={6}>
                {[0, 1].map(_ => (
                  <Skeleton
                    key={_}
                    variant="rectangular"
                    height={280}
                    sx={{ borderRadius: 2 }}
                  />
                ))}
              </Stack>
            }
          />
        </Stack>
      </ConsoleBaseLayout>
    </ArtistThemeProvider>
  )
}

type MembershipNoteCellProps = {
  loading: boolean
  requirePersonalInformation: boolean
  enableDigitalMembershipCard: boolean
  totalPlan?: number
  totalFavoriteMembers: number
  enableFavoriteMembers?: boolean
}
const MembershipNoteCell: Cell<MembershipNoteCellProps> = ({
  loading,
  requirePersonalInformation,
  enableDigitalMembershipCard,
  totalPlan,
  totalFavoriteMembers,
  enableFavoriteMembers,
}) => {
  const { t } = useTranslation('consoleMemberShip')

  if (loading) {
    return null
  }

  return (
    <CellFrame sx={{ pb: 2 }}>
      <Card elevation={0} sx={{ borderRadius: 2 }}>
        <CardContent sx={{ py: 3, px: 5, '&:last-child': { pb: 3 } }}>
          <Typography variant="subtitle1" color="text.secondary" pb={1}>
            {t('addedPlanNumber', { number: totalPlan || '-' })}
          </Typography>
          <Typography variant="subtitle1" color="text.secondary" pb={1}>
            {t('personalInfo')}
            {requirePersonalInformation ? t('collect') : t('uncollect')}
          </Typography>
          <Typography variant="subtitle1" color="text.secondary">
            {t('favoriteMembers', {
              status: enableFavoriteMembers ? t('enabled') : t('disabled'),
              number: totalFavoriteMembers,
            })}
          </Typography>
          <Typography variant="subtitle1" color="text.secondary">
            {t('digitalMembershipCardStatus', {
              status: enableDigitalMembershipCard
                ? t('enabled')
                : t('disabled'),
            })}
          </Typography>
        </CardContent>
      </Card>
    </CellFrame>
  )
}

type MembershipIconButtonProps = {
  icon: React.ReactNode
  label: string
  onClick?: () => void
}

const MembershipIconButton: React.FC<MembershipIconButtonProps> = ({
  icon,
  label,
  onClick,
}) => {
  return (
    <Stack alignItems="center" spacing={1}>
      <Box
        sx={{
          width: { xs: '100%', sm: 164 },
          height: { xs: 48, sm: 64 },
          borderRadius: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
        onClick={onClick}
      >
        <Box
          sx={{
            width: { xs: 36, sm: 44 },
            height: { xs: 36, sm: 44 },
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: 'grey.50',
            cursor: 'pointer',
            '&:hover': {
              bgcolor: 'grey.300',
            },
          }}
        >
          {icon}
        </Box>
      </Box>
      <Typography
        variant="subtitle1"
        color="text.secondary"
        textAlign="center"
        sx={{ fontSize: { xs: '10px', sm: '13px' } }}
      >
        {label}
      </Typography>
    </Stack>
  )
}

type MembershipIconsCellProps = {}
const MembershipIconsCell: Cell<MembershipIconsCellProps> = () => {
  const { t } = useTranslation('consoleMemberShip')
  const { routerPages, router } = useRouter()

  const iconButtons = [
    // TODO: UT-19975 カスタム項目管理を実装したら追加する
    // {
    //   icon: <Icons.Survey sx={{ fontSize: 24, color: 'grey.700' }} />,
    //   label: t('customItemManagement'),
    //   onClick: undefined, // 現在はクリックイベントなし
    // },
    {
      icon: <Icons.Group sx={{ fontSize: 24, color: 'grey.700' }} />,
      label: t('favoriteMemberSettings'),
      onClick: () => router.push(routerPages.ConsoleFavoriteMemberSettings),
    },
    {
      icon: <Icons.MembershipCard sx={{ fontSize: 24, color: 'grey.700' }} />,
      label: t('digitalMembershipCardSettings'),
      onClick: () => router.push(routerPages.ConsoleDigitalMembershipCard),
    },
  ]

  return (
    <CellFrame sx={{ pb: 2, pt: 2 }}>
      <Card elevation={0} sx={{ backgroundColor: 'transparent' }}>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: `repeat(${iconButtons.length}, 1fr)`,
            gap: 2,
            p: 2,
          }}
        >
          {iconButtons.map((button, index) => (
            <MembershipIconButton
              key={index}
              icon={button.icon}
              label={button.label}
              onClick={button.onClick}
            />
          ))}
        </Box>
      </Card>
    </CellFrame>
  )
}

type FixedBottomCellProps = {
  disabled: boolean
}
const FixedBottomCell: Cell<FixedBottomCellProps> = ({ disabled }) => {
  const { t } = useTranslation('consoleMemberShip')
  const { routerPages } = useRouter()

  return (
    <BottomSafeArea sx={{ bgcolor: 'grey.50' }}>
      <CellFrame sx={{ py: 4, bgcolor: 'grey.50', textAlign: 'center' }}>
        <Button
          size="large"
          sx={{ minWidth: 244 }}
          disabled={disabled}
          href={routerPages.ConsoleMembershipCreate}
        >
          {t('addMembership')}
        </Button>
      </CellFrame>
    </BottomSafeArea>
  )
}
